import { useCallback, useMemo, useState } from "react";
import {
  Alert,
  Box,
  DialogActions,
  DialogContent,
  Link,
  Typography
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faDownload } from "@fortawesome/free-solid-svg-icons";
import DOMPurify from "dompurify";
import DialogComponent from "../../Dialog/Dialog";
import ActionButton from "../../Button/ActionButton";
import Loading from "../../Loading/Loading";
import useProfessionalAccountStatus from "../../../hooks/useProfessionalAccountStatus";
import usePaymentTermsContent from "../../../hooks/usePaymentTermsContent";
import {
  PAYMENT_TERMS_PDF_FILENAME,
  PAYMENT_TERMS_PDF_URL,
  PAYMENT_TERMS_URL
} from "../../../constants";
import { semanticColors } from "../../../utils/colors";
import { typography } from "../../../utils/typography";

/**
 * Only the prose survives. The policy is authored in a page builder whose
 * wrappers, classes and inline styles describe a marketing page, not a dialog —
 * keeping them would drag that layout in here. Dropping every attribute but
 * `href` also means nothing arrives that could restyle the app around it.
 */
const ALLOWED_TAGS = [
  "p",
  "h2",
  "h3",
  "h4",
  "h5",
  "ul",
  "ol",
  "li",
  "strong",
  "b",
  "em",
  "i",
  "a",
  "br",
  "sup"
];

/**
 * Sanitises the policy, sends its real links to a new tab, and strips the ones
 * that lead nowhere.
 *
 * The policy opens with an index of its own sections, written as in-page
 * anchors. On the marketing site those jump down the page; here they cannot —
 * the sections scroll inside this dialog and their ids do not survive
 * sanitising. A link that looks like a link and does nothing is worse than
 * plain text, so those anchors are unwrapped and only their words remain.
 *
 * The rewrite runs AFTER sanitising and only ever adds attributes or removes
 * elements, so it cannot widen what DOMPurify allowed through.
 */
function sanitisePolicy(html: string): string {
  const clean = DOMPurify.sanitize(html, {
    ALLOWED_TAGS,
    ALLOWED_ATTR: ["href"]
  });
  const template = document.createElement("template");
  template.innerHTML = clean;

  template.content.querySelectorAll("a").forEach(anchor => {
    const href = anchor.getAttribute("href") ?? "";
    if (/^https?:\/\//i.test(href)) {
      anchor.setAttribute("target", "_blank");
      // Without noopener the opened page can navigate this one (tabnabbing).
      anchor.setAttribute("rel", "noopener noreferrer");
      return;
    }
    anchor.replaceWith(...Array.from(anchor.childNodes));
  });

  return template.innerHTML;
}

/** Matches the label beside it — the secondary button reads in the dark ink. */
const saveIconColor = semanticColors.text.primary;

/**
 * The policy reads as a document, so it keeps a document's rhythm.
 *
 * The frame is not decoration: the text runs far past what fits, and without an
 * edge the page looks as though it simply ends where the box does. Bounding it
 * says there is more inside, and the scrollbar it holds says which way.
 */
const policySx = {
  ...typography.body.m,
  color: semanticColors.text.primary,
  maxHeight: "55vh",
  overflowY: "auto",
  border: `1px solid ${semanticColors.border.light}`,
  borderRadius: "8px",
  p: 2,
  "& h2, & h3, & h4, & h5": {
    ...typography.body.lBold,
    color: semanticColors.text.primary,
    mt: 3,
    mb: 1
  },
  "& p": { mb: 1.5 },
  "& ul, & ol": { pl: 3, mb: 1.5 },
  "& li": { mb: 0.5 },
  "& a": { color: semanticColors.text.link }
};

/**
 * The payment terms, and the single action of agreeing to them.
 *
 * Closing is just closing: the dialog offers no way to decline, because
 * declining is not a decision the professional has to record — not agreeing is
 * simply not proceeding. Whoever opened this decides what closing means, which
 * is what lets the same dialog serve the first step of registering an account
 * and the reconfirmation after the terms are revised.
 *
 * The policy is read in place. When it cannot be fetched the dialog falls back
 * to linking out, which is where it was read before, so a failure never leaves
 * the professional agreeing to something they had no way of seeing.
 */
export default function PaymentTermsDialog({
  open,
  onClose,
  onAccepted
}: {
  open: boolean;
  onClose: () => void;
  /** Called once the acceptance has been recorded and the profile reread. */
  onAccepted?: () => void;
}) {
  const { t } = useTranslation();
  const { acceptPaymentTerms } = useProfessionalAccountStatus();
  const { html, loading: loadingPolicy } = usePaymentTermsContent();
  const [accepting, setAccepting] = useState(false);
  const [acceptError, setAcceptError] = useState(false);

  const policy = useMemo(() => (html ? sanitisePolicy(html) : null), [html]);

  const onAccept = useCallback(async () => {
    try {
      setAccepting(true);
      setAcceptError(false);
      await acceptPaymentTerms();
      onAccepted?.();
    } catch {
      // The dialog stays open; without this the retry would be invisible.
      setAcceptError(true);
    } finally {
      setAccepting(false);
    }
  }, [acceptPaymentTerms, onAccepted]);

  return (
    <DialogComponent
      open={open}
      handleModal={onClose}
      headerTxt={t("paymentTerms.title")}
      maxWidth="md"
    >
      <DialogContent>
        {loadingPolicy ? (
          <Box display="flex" justifyContent="center" py={4}>
            <Loading />
          </Box>
        ) : policy ? (
          <Box sx={policySx} dangerouslySetInnerHTML={{ __html: policy }} />
        ) : (
          /* Says why the text is missing and leaves the link below to stand in
             for it, rather than a bare link with nothing explaining itself. */
          <Typography
            sx={{ ...typography.body.l, color: semanticColors.text.primary }}
          >
            {t("paymentTerms.unavailable")}
          </Typography>
        )}

        <Box sx={{ mt: 2 }}>
          <Link
            href={PAYMENT_TERMS_URL}
            target="_blank"
            rel="noopener noreferrer"
          >
            {t("paymentTerms.openDetails")}
          </Link>
        </Box>

        {acceptError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {t("paymentTerms.acceptError")}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Box display="flex" gap={1} p={1} width="100%">
          {/* Always offered: the file is served by this app, so it is there
              even when the policy text could not be fetched for the box above. */}
          <ActionButton
            label={t("paymentTerms.downloadPdf")}
            href={PAYMENT_TERMS_PDF_URL}
            download={PAYMENT_TERMS_PDF_FILENAME}
            variant="secondary"
            startIcon={
              <FontAwesomeIcon icon={faDownload} color={saveIconColor} />
            }
          />
          {/* Agreeing stays on the right whether or not the save button is
              there, so the primary action never shifts under the cursor. */}
          <Box sx={{ ml: "auto" }}>
            <ActionButton
              label={t("paymentTerms.accept")}
              onClick={onAccept}
              loading={accepting}
            />
          </Box>
        </Box>
      </DialogActions>
    </DialogComponent>
  );
}
